import {ImageService} from './base-service'

/**
 * Brightcove CDN Service for video poster images
 * Requires '/match/' path segment for "best fit" scaling mode
 */
export class BrightcoveService extends ImageService {
    /**
     * Create Brightcove service instance if URL is supported
     * @param {string} url - Image URL (must include '/match/' path segment)
     * @param {Object} _context - Context (not used for Brightcove)
     * @returns {BrightcoveService|null} Service instance or null
     */
    static create(url, _context) {
        if (!url || typeof url !== 'string') return null
        if (!url.includes('cf-images.') || !url.includes('.boltdns.net/v1/jit/')) return null

        // Require '/match/' path segment - this is Brightcove's scaling mode
        if (!url.includes('/match/')) return null

        try {
            // Extract base URL, resolution, and timestamp
            // Pattern: {baseUrl}/main/{width}x{height}/{timestamp}/match/image.jpg
            const match = url.match(
                /^(https:\/\/cf-images\.[^/]+\/v1\/jit\/[^/]+\/[^/]+\/main)\/(\d+)x(\d+)\/([^/]+)\/match/
            )
            if (!match) return null

            const width = parseInt(match[2])
            const height = parseInt(match[3])
            const aspectRatio = width / height

            const baseInfo = {
                baseUrl: match[1],
                timestamp: match[4],
                aspectRatio
            }

            // Pass the original URL as the service-ready URL
            // Brightcove URLs don't require conversion - they're already in service format
            return new BrightcoveService(url, baseInfo)
        } catch {
            return null
        }
    }

    generateUrl(width, _options = {}) {
        const {baseUrl, timestamp} = this.baseInfo

        if (!baseUrl || !width || width <= 0 || !timestamp) return null

        // Always use 3000px height to ensure we get the exact width requested
        // Brightcove scales to fit, so large height ensures width is honored
        return `${baseUrl}/${width}x3000/${timestamp}/match/image.jpg`
    }

    getSupportedFormats() {
        // Brightcove CDN only supports JPEG
        return ['image/jpeg']
    }

    generateSources(sizes) {
        if (!sizes || sizes.length === 0) {
            // No sizes specified, generate single URL
            return [
                {
                    type: 'image/jpeg',
                    srcSet: this.generateUrl(1200)
                }
            ]
        }

        // Generate srcset entries for all sizes
        const srcsetEntries = sizes
            .map((width) => {
                const url = this.generateUrl(width)
                return url ? `${url} ${width}w` : null
            })
            .filter(Boolean)

        return [
            {
                type: 'image/jpeg',
                srcSet: srcsetEntries.join(', ')
            }
        ]
    }

    /**
     * Extract width from a Brightcove CDN URL path
     * @param {string} url - Brightcove image URL
     * @returns {number|null} Width in pixels or null if not found/invalid
     *
     * @example
     * service.extractWidthFromUrl('https://cf-images.../main/800x3000/15s61ms/match/image.jpg')
     * // Returns: 800
     */
    extractWidthFromUrl(url) {
        if (!url || typeof url !== 'string') return null

        // Extract width from path pattern: /main/{width}x{height}/{timestamp}/match/
        const match = url.match(/\/main\/(\d+)x\d+\/[^/]+\/match\//)
        if (!match) return null

        const width = parseInt(match[1], 10)
        return Number.isNaN(width) ? null : width
    }
}
